/**
 * 強制改密碼頁（mustChangePassword=yes）— App 層直接渲染，不進 AppShell。
 * 管理員建立或重設密碼後，使用者拿臨時密碼登入會停在這裡，改完才放行。
 */
import { LogOut, ShieldAlert } from "lucide-react";
import { trpc, queryClient } from "../../lib/trpc";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../contexts/LanguageContext";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { LogoImage } from "../../components/Logo";
import { ThemeToggle } from "../../components/theme-toggle";
import { ChangePasswordForm } from "../../components/account/ChangePasswordForm";

export default function ForcePasswordChange() {
  const { t } = useLanguage();
  const { user, refetch } = useAuth();
  const logout = trpc.auth.logout.useMutation({
    onSuccess: () => {
      queryClient.clear();
      refetch();
    },
  });

  return (
    <div className="min-h-screen bg-background">
      <header className="flex items-center justify-between border-b border-border px-6 py-4 md:px-10">
        <LogoImage className="h-8" />
        <div className="flex items-center gap-2">
          <ThemeToggle />
          <Button variant="outline" size="sm" onClick={() => logout.mutate()}>
            <LogOut className="h-3.5 w-3.5" />
            {t({ "zh-TW": "登出", "en-US": "Sign out", "zh-CN": "登出" })}
          </Button>
        </div>
      </header>
      <main className="mx-auto max-w-md px-6 py-12">
        <div className="animate-fade-up space-y-6">
          <div className="space-y-2">
            <p className="font-mono text-[12px] uppercase tracking-[0.18em] text-primary">
              {t({ "zh-TW": "首次登入", "en-US": "First sign-in", "zh-CN": "首次登入" })}
            </p>
            <h1 className="font-display text-3xl font-medium tracking-tight">
              {t({ "zh-TW": "請先設定新密碼", "en-US": "Set a new password", "zh-CN": "请先设定新密码" })}
            </h1>
            <p className="text-[13px] leading-relaxed text-muted-foreground">
              {t({
                "zh-TW": `${user?.email ?? ""} 的密碼由平台管理員設定，為了帳號安全，請改成只有你知道的密碼後再繼續。`,
                "en-US": `The password for ${user?.email ?? ""} was set by a platform admin. Please choose your own before continuing.`,
                "zh-CN": `${user?.email ?? ""} 的密码由平台管理员设定，为了帐号安全，请改成只有你知道的密码后再继续。`,
              })}
            </p>
          </div>
          <Card>
            <CardContent className="space-y-4 p-6">
              <div className="flex items-start gap-2 rounded-md border border-[color-mix(in_oklab,var(--color-warning)_35%,transparent)] bg-[color-mix(in_oklab,var(--color-warning)_15%,transparent)] px-3 py-2 text-[12px] text-[var(--color-warning)]">
                <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                {t({
                  "zh-TW": "改密碼前，系統其他功能暫時無法使用。",
                  "en-US": "Other features are locked until the password is changed.",
                  "zh-CN": "改密码前，系统其他功能暂时无法使用。",
                })}
              </div>
              <ChangePasswordForm
                currentLabel={t({ "zh-TW": "臨時密碼（管理員提供）", "en-US": "Temporary password (from admin)", "zh-CN": "临时密码（管理员提供）" })}
                onSuccess={() => {
                  queryClient.clear();
                  refetch();
                }}
              />
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
